import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { AspectRatio } from '@lumen5/luminary';

import type { SceneDesign } from 'js/src/client';
import { getFullscreenSelectorSceneDesigns } from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';

/** Most previews a single preview_scene_designs call will render, to bound offscreen render cost. */
export const PREVIEW_SCENE_DESIGNS_CAP = 3;

const RATIO_DATA_KEY: Record<AspectRatio, keyof SceneDesign> = {
    [AspectRatio.LANDSCAPE]: 'landscape_data',
    [AspectRatio.SQUARE]: 'square_data',
    [AspectRatio.VERTICAL]: 'vertical_data',
    [AspectRatio.FOURFIVE]: 'fourfive_data',
};

/**
 * The template's scene designs the browsing tools offer: fullscreen layout designs — caption,
 * talking-head, and voiceover designs are excluded, they aren't the template's layout language —
 * that actually have data for `aspectRatio`. The ratio filter matters both for real scoping and for
 * safety: a design with no `<ratio>_data` throws when instantiated offscreen, which would fail the
 * whole preview batch.
 */
export function getBrowsableSceneDesigns(
    state: RootState,
    templateId: string,
    aspectRatio: AspectRatio,
): SceneDesign[] {
    return getFullscreenSelectorSceneDesigns(state, templateId).filter(
        (design) => design[RATIO_DATA_KEY[aspectRatio]] != null,
    );
}

/**
 * Render the design catalog as plain text (no images): a scannable list the agent reads to choose a
 * few ids to preview. Designs are grouped by display group (groups ordered by id, designs by their
 * `order`) and listed by display name (falling back to the id) with their scene_design_id. Pure over
 * the design list + a group-name resolver.
 */
export function formatDesignCatalog(
    designs: SceneDesign[],
    getGroupName: (groupId: number) => string,
): string {
    const lines: string[] = [
        `${designs.length} scene design${designs.length === 1 ? '' : 's'} in this template. Scan ` +
            `the list, then preview a few relevant ones with ${TOOL_NAMES.previewSceneDesigns} to see how they ` +
            'look and how they are built.',
    ];

    const byGroup = new Map<number, SceneDesign[]>();
    for (const design of designs) {
        const group = byGroup.get(design.display_group) ?? [];
        group.push(design);
        byGroup.set(design.display_group, group);
    }

    for (const groupId of [...byGroup.keys()].sort((a, b) => a - b)) {
        const groupDesigns = [...byGroup.get(groupId)!].sort((a, b) => a.order - b.order);
        const groupName = getGroupName(groupId).trim() || `Display Group ${groupId}`;
        lines.push('', `### ${groupName} (${groupDesigns.length})`);
        for (const design of groupDesigns) {
            const name = design.display_name?.trim();
            lines.push(`- ${name ? `${name} — ` : ''}id: ${design.id}`);
        }
    }
    return lines.join('\n');
}
